// @ts-nocheck
import React from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalNavigation from '../components/common/GlobalNavigation';
import { KeyboardShortcutsModal } from '../components/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useSwitchUser } from '../contexts/SwitchUserContext';
import { persistedTaskRecords } from '../services/mockData/tasks';
import { persistedClientRecords } from '../services/mockData/clients';
import { useGobodoRecords, useRecordCount } from '../services/queries/gobodoRecordQueries';
import { shouldUseGobodo } from '../utils/gobodoUtils';
import { AlertCircle, CheckCircle, Clock, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMVPDefaultRole } from '../config/mvpConfig';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { isSwitched } = useSwitchUser();
  const { isShortcutsModalOpen, setIsShortcutsModalOpen } = useKeyboardShortcuts();
  const contentPadding24 = isSwitched ? 'pt-[140px]' : 'pt-24';

  const getUserRole = (): string => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };

  const userRole = getUserRole();

  const handleAppSelect = (app: any) => {
    if (app.name === 'home') {
      navigate('/');
    } else {
      navigate(`/${app.name.toLowerCase().replace(' ', '-')}`);
    }
  };

  const handleNavigateToTask = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleNavigateToCase = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoTasks } = useGobodoRecords('Task__c');
  const { data: gobodoClients } = useGobodoRecords('Client__c');
  const { data: taskCount } = useRecordCount('Task__c');
  const { data: clientCount } = useRecordCount('Client__c');

  const dashboardTasks = (shouldGobodo && gobodoTasks) ? gobodoTasks : persistedTaskRecords.get();
  const dashboardClients = (shouldGobodo && gobodoClients) ? gobodoClients : persistedClientRecords.get();

  const tStatus = (t: any) => t?.status ?? t?.Status__c;
  const tPriority = (t: any) => t?.priority ?? t?.Priority__c;
  const tDueDate = (t: any) => t?.dueDate ?? t?.DueDate__c;
  const cStatus = (c: any) => c?.status ?? c?.Status__c;

  const getTeamStats = () => {
    const totalTasks = (typeof taskCount === 'number' ? taskCount : null) ?? dashboardTasks.length;
    const totalClients = (typeof clientCount === 'number' ? clientCount : null) ?? dashboardClients.length;
    const completedTasks = dashboardTasks.filter((t: any) => tStatus(t) === 'Completed').length;
    const highPriorityTasks = dashboardTasks.filter((t: any) => tPriority(t) === 'High' && tStatus(t) !== 'Completed').length;
    const overdueTasks = dashboardTasks.filter((t: any) => {
      const due = tDueDate(t);
      if (!due) return false;
      const dueDate = new Date(due);
      const today = new Date();
      return dueDate < today && tStatus(t) !== 'Completed';
    }).length;

    return {
      totalTasks,
      completedTasks,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      highPriorityTasks,
      overdueTasks,
      activeClients: dashboardClients.filter((c: any) => cStatus(c) === 'Active').length,
      totalClients
    };
  };

  const getTeamPerformance = () => {
    const uniqueAssignees = new Set(dashboardTasks.map((t: any) => t.assignee ?? t.Assignee__c).filter(Boolean));
    const assignees = Array.from(uniqueAssignees);
    return assignees.map((assignee: string) => {
      const tasks = dashboardTasks.filter((t: any) => (t.assignee ?? t.Assignee__c) === assignee);
      const completed = tasks.filter((t: any) => tStatus(t) === 'Completed').length;
      const total = tasks.length;
      return {
        name: assignee,
        completed,
        total,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        highPriority: tasks.filter((t: any) => tPriority(t) === 'High' && tStatus(t) !== 'Completed').length
      };
    });
  };

  const teamStats = getTeamStats();
  const teamPerformance = getTeamPerformance();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <GlobalNavigation
        currentView="dashboard"
        onNavigate={(view) => {
          if (view === 'home') {
            navigate('/');
          } else {
            navigate(`/${view}`);
          }
        }}
        userRole={userRole}
        onRoleChange={() => {}}
        showMainNavigation={true}
        onAppSelect={handleAppSelect}
        onNavigateToTask={handleNavigateToTask}
        onNavigateToCase={handleNavigateToCase}
      />

      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${contentPadding24}`}>
        <div className="mb-8 flex items-center gap-4">
          <img 
            src="/regal-logo.svg" 
            alt="Regal Product Hub" 
            className="w-12 h-12"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Team Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">Overview of team performance and metrics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completion Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.completionRate}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{teamStats.completedTasks} of {teamStats.totalTasks} tasks</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Clients</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.activeClients}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">of {teamStats.totalClients} total</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.highPriorityTasks}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">urgent items</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Overdue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.overdueTasks}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">past due</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Team Performance</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {teamPerformance.map((member) => (
                  <div key={member.name} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{member.completionRate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${member.completionRate}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>{member.completed}/{member.total} completed</span>
                        {member.highPriority > 0 && (
                          <span className="text-red-600 dark:text-red-400">{member.highPriority} high priority</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Priority Distribution</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {['High', 'Medium', 'Low'].map((priority) => {
                  const count = dashboardTasks.filter((t: any) => tPriority(t) === priority && tStatus(t) !== 'Completed').length;
                  const total = dashboardTasks.filter((t: any) => tStatus(t) !== 'Completed').length;
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

                  return (
                    <div key={priority} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{priority} Priority</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{count} tasks</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              priority === 'High' ? 'bg-red-500' :
                              priority === 'Medium' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Team Activity</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                {(() => {
                  const sortedTasks = [...dashboardTasks].sort((a: any, b: any) => {
                    const dateA = new Date(a.lastModifiedDate ?? a.createdDate ?? a.lastModified ?? '');
                    const dateB = new Date(b.lastModifiedDate ?? b.createdDate ?? b.lastModified ?? '');
                    return dateB.getTime() - dateA.getTime();
                  });
                  return sortedTasks.slice(0, 10);
                })().map((task: any) => (
                  <tr key={task.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{task.title ?? task.Subject__c ?? task.subject}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{task.assignee ?? task.Assignee__c}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tStatus(task) === 'Completed' ? 'bg-green-100 text-green-800' :
                        tStatus(task) === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {tStatus(task)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tPriority(task) === 'High' ? 'bg-red-100 text-red-800' :
                        tPriority(task) === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {tPriority(task)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{tDueDate(task)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />
    </div>
  );
};

export default DashboardPage;
